import { ImageStyleProperties } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/ImageElement';
import { LottieStyleProperties } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/LottieElement';
import { getEnvConfig } from '@/constants';

export const ASSET_PATH = 'content-lab/filestack/clip_asset_overlay';

export const DEFAULT_IMAGE_DURATION = 20;
export const DEFAULT_VIDEO_DURATION = 30;
export const DEFAULT_AUDIO_DURATION = 30;
export const DEFAULT_LOTTIE_DURATION = 3;
export const DEFAULT_IMAGE_SIZE = 30;
export const DEFAULT_VIDEO_SIZE = 80;

// Music Library Constants
const STATIC_ASSETS_CDN = getEnvConfig('STATIC_ASSETS_CDN') || 'https://d6d4ismr40iw.cloudfront.net';
const MUSIC_BASE_URL = `${STATIC_ASSETS_CDN}/bgm-music`;

export interface MusicTrack {
  id: string;
  name: string;
  artist: string;
  description: string;
  url: string;
  filename: string; // Just the filename (e.g., 'love-romantic-hopeful-music-333017.mp3')
  categories: string[];
  duration?: number; // Duration in seconds (optional, can be fetched dynamically)
}

export const MUSIC_LIBRARY: Record<string, MusicTrack> = {
  'bgm-1': {
    id: 'bgm-1',
    name: 'Romantic Cinematic',
    artist: 'provided by Pixabay',
    description: 'Warm piano & strings',
    url: `${MUSIC_BASE_URL}/love-romantic-loopable.mp3`,
    filename: 'love-romantic-loopable.mp3',
    categories: ['romantic', 'cinematic', 'calm']
  },
  'bgm-2': {
    id: 'bgm-2',
    name: 'Comedy Cartoon',
    artist: 'provided by Pixabay',
    description: 'Funny, bouncy animated',
    url: `${MUSIC_BASE_URL}/funny-comedy-cartoon-loopable.mp3`,
    filename: 'funny-comedy-cartoon-loopable.mp3',
    categories: ['comedy', 'upbeat', 'fun']
  },
  'bgm-3': {
    id: 'bgm-3',
    name: 'Afro Dancehall',
    artist: 'provided by Pixabay',
    description: 'Energetic rhythmic beats',
    url: `${MUSIC_BASE_URL}/whip-afro-dancehall-loopable.mp3`,
    filename: 'whip-afro-dancehall-loopable.mp3',
    categories: ['upbeat', 'energetic', 'dance']
  },
  'bgm-4': {
    id: 'bgm-4',
    name: 'Cinematic Pop',
    artist: 'provided by Pixabay',
    description: 'Uplifting motivational',
    url: `${MUSIC_BASE_URL}/showreel-promo-loopable.mp3`,
    filename: 'showreel-promo-loopable.mp3',
    categories: ['upbeat', 'motivational', 'cinematic', 'corporate']
  },
  'bgm-5': {
    id: 'bgm-5',
    name: 'Corporate',
    artist: 'provided by Pixabay',
    description: 'Calm professional',
    url: `${MUSIC_BASE_URL}/music-corporate-no-lead-loopable.mp3`,
    filename: 'music-corporate-no-lead-loopable.mp3',
    categories: ['corporate', 'calm', 'professional']
  },
  'bgm-6': {
    id: 'bgm-6',
    name: 'Ambient Chill',
    artist: 'provided by Pixabay',
    description: 'Relaxed dreamy',
    url: `${MUSIC_BASE_URL}/music-no-copyright-loopable.mp3`,
    filename: 'music-no-copyright-loopable.mp3',
    categories: ['ambient', 'calm', 'chill']
  },
  'bgm-7': {
    id: 'bgm-7',
    name: 'Phonk',
    artist: 'provided by Pixabay',
    description: 'Dark aggressive energy',
    url: `${MUSIC_BASE_URL}/phonk-loopable.mp3`,
    filename: 'phonk-loopable.mp3',
    categories: ['upbeat', 'energetic', 'aggressive']
  },
  'bgm-8': {
    id: 'bgm-8',
    name: 'Nature Soundscape',
    artist: 'provided by Pixabay',
    description: 'Peaceful forest sounds',
    url: `${MUSIC_BASE_URL}/morning-in-the-forest-loopable.mp3`,
    filename: 'morning-in-the-forest-loopable.mp3',
    categories: ['calm', 'nature', 'ambient']
  },
  'bgm-9': {
    id: 'bgm-9',
    name: 'Chill Jazz',
    artist: 'provided by Pixabay',
    description: 'Smooth lounge style',
    url: `${MUSIC_BASE_URL}/just-relax-loopable.mp3`,
    filename: 'just-relax-loopable.mp3',
    categories: ['calm', 'jazz', 'chill']
  },
  'bgm-10': {
    id: 'bgm-10',
    name: 'Drum & Bass',
    artist: 'provided by Pixabay',
    description: 'High-energy electronic',
    url: `${MUSIC_BASE_URL}/jungle-waves-loopable.mp3`,
    filename: 'jungle-waves-loopable.mp3',
    categories: ['upbeat', 'energetic', 'electronic']
  },
  'bgm-11': {
    id: 'bgm-11',
    name: 'Acoustic Folk',
    artist: 'provided by Pixabay',
    description: 'Gentle warm acoustic',
    url: `${MUSIC_BASE_URL}/by-the-riverside-loopable.mp3`,
    filename: 'by-the-riverside-loopable.mp3',
    categories: ['calm', 'acoustic', 'folk', 'corporate']
  },
  'bgm-12': {
    id: 'bgm-12',
    name: 'Soft Ambient',
    artist: 'provided by Pixabay',
    description: 'Minimal peaceful',
    url: `${MUSIC_BASE_URL}/calm-soft-bg-music-loopable.mp3`,
    filename: 'calm-soft-bg-music-loopable.mp3',
    categories: ['calm', 'ambient', 'peaceful']
  }
};

export const MUSIC_CATEGORIES = {
  upbeat: ['bgm-2', 'bgm-3', 'bgm-4', 'bgm-7', 'bgm-10'],
  calm: ['bgm-1', 'bgm-5', 'bgm-6', 'bgm-8', 'bgm-9', 'bgm-11', 'bgm-12'],
  corporate: ['bgm-4', 'bgm-5', 'bgm-11'],
  energetic: ['bgm-3', 'bgm-7', 'bgm-10'],
  ambient: ['bgm-6', 'bgm-8', 'bgm-12'],
  cinematic: ['bgm-1', 'bgm-4']
};

// Music Search Helper Functions
export const searchMusicByCategory = (category: string): MusicTrack[] => {
  const categoryLower = category.toLowerCase();
  return Object.values(MUSIC_LIBRARY).filter(track =>
    track.categories.some(cat => cat.toLowerCase().includes(categoryLower))
  );
};

export const findMusicByName = (name: string): MusicTrack | null => {
  const nameLower = name.toLowerCase();
  return Object.values(MUSIC_LIBRARY).find(track => track.name.toLowerCase().includes(nameLower)) || null;
};

export const getSimilarMusic = (query: string): string[] => {
  const queryLower = query.toLowerCase();
  const queryWords = queryLower.split(/\s+/);

  // Score tracks based on matching words in name or categories
  const scored = Object.values(MUSIC_LIBRARY).map(track => {
    let score = 0;
    const trackText = `${track.name} ${track.categories.join(' ')}`.toLowerCase();

    queryWords.forEach(word => {
      if (trackText.includes(word)) {
        score++;
      }
    });

    return { track, score };
  });

  // Filter tracks with at least one match and sort by score
  return scored
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(item => item.track.name);
};

/**
 * Find a music track by URL, comparing by filename instead of full URL.
 * This handles cases where the backend might use a different CDN alias.
 *
 * @param url - The full URL or partial URL of the music file
 * @returns The music track object if found, null otherwise
 */
export const findMusicByUrl = (url: string): MusicTrack | null => {
  if (!url) {
    return null;
  }

  // Extract filename from URL (everything after the last /)
  const urlFilename = url.split('/').pop()?.toLowerCase() || '';

  if (!urlFilename) {
    return null;
  }

  // Find track by matching filename
  const match = Object.entries(MUSIC_LIBRARY).find(([_, track]) => track.filename.toLowerCase() === urlFilename);

  return match ? match[1] : null;
};

export const IMAGE_ELEMENT_BORDER_RADIUS_DEFAULT = '0px';
export const IMAGE_ELEMENT_BORDER_RADIUS_FULL = '40px';

export const DEFAULT_IMAGE_STYLE: ImageStyleProperties = {
  width: '100%',
  height: '100%',
  borderRadius: IMAGE_ELEMENT_BORDER_RADIUS_DEFAULT,
  objectFit: 'contain'
};

export const DEFAULT_VIDEO_STYLE = {
  width: '100%',
  height: '100%',
  borderRadius: '0px',
  objectFit: 'cover'
};

export const DEFAULT_CONTAINER_STYLE: ImageStyleProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: `${DEFAULT_IMAGE_SIZE}%`,
  height: `${DEFAULT_IMAGE_SIZE}%`
};

export const DEFAULT_VIDEO_CONTAINER_STYLE: ImageStyleProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%'
};

export const DEFAULT_LOTTIE_CONTAINER_STYLE: LottieStyleProperties = {
  position: 'absolute',
  top: '20%',
  left: '10%',
  width: `80%`,
  height: `60%`
};

export const DEFAULT_LOTTIE_STYLE: LottieStyleProperties = {
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  borderRadius: '0px',
  opacity: 1,
  preserveAspectRatio: 'xMidYMid meet'
};
